/**
 * NMS Size Popup Component
 *
 * Reusable size selection popup for product cards and other components
 * Handles size selection in a modal overlay with accessibility support
 *
 * Built following NMS JavaScript standards - extends NMSBaseClass
 */

class NMSSizePopup extends window.NMSBaseClass {
  constructor(options = {}) {
    super();

    // Configuration
    this.productId = options.productId;
    this.productData = options.productData;
    this.onSelect = options.onSelect || (() => {});
    this.onClose = options.onClose || (() => {});
    this.triggerElement = options.triggerElement;
    this.containerElement = options.containerElement || document.body;

    // State
    this.isOpen = false;
    this.selectedSize = null;

    // Create content element (not backdrop - using standardized one)
    this.element = this.#createContentElement();
    this.containerElement.appendChild(this.element);

    // Bind methods
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
  }

  /**
   * Initialize the popup
   */
  init() {
    // The base class init() expects this.element to be set, but it also
    // expects to scan the document by default. Since we create our element
    // dynamically, we need to manually set up refs after creation.
    this.#setupRefs();
    this.#setupEventListeners();
  }

  /**
   * Setup refs from data-ref attributes in our element
   * @private
   */
  #setupRefs() {
    // Initialize refs object from base class
    this.refs = this.refs || {};

    // Manually scan our popup element for data-ref attributes
    const refElements = this.element.querySelectorAll('[data-ref]');

    for (const element of refElements) {
      const refName = element.getAttribute('data-ref');

      if (refName) {
        this.refs[refName] = element;
      }
    }

    // Verify critical refs are found
    if (!this.refs.sizesContainer) {
      console.error('[NMS] Size popup: sizesContainer ref not found');
    }
  }

  /**
   * Create the popup content element (backdrop handled by standardized system)
   * @returns {HTMLElement} Content element
   * @private
   */
  #createContentElement() {
    const content = document.createElement('div');
    content.className = 'nms-size-popup';
    content.setAttribute('data-ref', 'popup');
    content.hidden = true;

    content.innerHTML = `
      <div class="nms-size-popup__container" data-ref="container">
        <div class="nms-size-popup__header">
          <span class="nms-size-popup__title nms-text-heading-sm">Select Size (UK)</span>
          <button
            type="button"
            class="nms-size-popup__close"
            data-ref="closeButton"
            aria-label="Close size selection"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              fill="none"
              width="20"
              class="nms-icon nms-icon--cross"
              viewBox="0 0 14 14"
              role="img"
              aria-label="Close"
            >
              <path
                d="M12.625 12.625L1.375 1.375"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M1.375 12.625L12.625 1.375"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div class="nms-size-popup__sizes" data-ref="sizesContainer">
          <!-- Sizes will be rendered here -->
        </div>
      </div>
    `;

    return content;
  }

  /**
   * Setup event listeners
   * @private
   */
  #setupEventListeners() {
    // Close button
    if (this.refs.closeButton) {
      this.refs.closeButton.addEventListener('click', this.hide);
      
      // Add touch event handling to override cart drawer interference
      this.refs.closeButton.addEventListener('touchend', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      }, { passive: false });
    }
  }

  /**
   * Show the size popup using standardized overlay backdrop
   */
  show() {
    if (!this.productData) {
      console.error('[NMS] Size popup: No product data provided');
      return;
    }

    // Use standardized overlay backdrop with callback
    this.showOverlayBackdrop({
      zIndex: 101,
      closeOnBackdrop: true,
      closeOnEscape: true,
      onClose: () => this.hide(), // Callback when overlay closes
    });

    // Render sizes
    this.#renderSizes();

    // Show content
    this.element.hidden = false;
    this.isOpen = true;

    // Backward compatibility: still set the old flag for any legacy code
    window.neemans.sizePopupOpen = true;

    // Add show class for animation
    setTimeout(() => {
      this.element.classList.add('nms-size-popup--show');
    }, 10);

    // Focus first available size
    const firstSize = this.element.querySelector('.nms-size-popup__size-input:not([disabled])');
    if (firstSize) {
      firstSize.focus();
    }

    // Store trigger for focus return
    this.previousFocus = document.activeElement;
  }

  /**
   * Hide the size popup and standardized overlay backdrop
   */
  hide() {
    if (!this.isOpen) return;

    // Hide standardized overlay backdrop
    this.hideOverlayBackdrop();

    // Hide content
    this.element.classList.remove('nms-size-popup--show');
    this.isOpen = false;

    // Clear backward compatibility flag
    if (window.neemans) {
      window.neemans.sizePopupOpen = false;
    }

    // Hide after animation
    setTimeout(() => {
      this.element.hidden = true;
    }, 200);

    // Return focus to trigger
    if (this.previousFocus) {
      this.previousFocus.focus();
    }

    // Call onClose callback
    if (this.onClose) {
      this.onClose();
    }
  }

  /**
   * Render size options (Phase 4: Optimized with precomputed data)
   * @private
   */
  #renderSizes() {
    // Use current product data's ID instead of original productId for correct size lookup
    const currentProductId = this.productData?.id || this.productId;

    // Phase 4: Try to use optimized global data first
    const globalSizeData = window.neemans?.productCards?.[currentProductId]?.sizeOption;

    if (globalSizeData && globalSizeData.values && globalSizeData.availability) {
      // Use precomputed size data for maximum performance
      const sizesHTML = globalSizeData.values
        .map((size, index) => {
          const isAvailable = globalSizeData.availability[size] === true;
          const inputId = `SizePopup-${currentProductId}-${index}`;

          return `
          <label class="nms-size-popup__size-label${!isAvailable ? ' nms-size-popup__size-label--disabled' : ''}">
            <input
              type="radio"
              name="popup-size"
              value="${size}"
              id="${inputId}"
              class="nms-size-popup__size-input sr-only"
              data-size="${size}"
              data-option-position="${globalSizeData.position}"
              ${!isAvailable ? 'disabled' : ''}
            >
            <span
              class="nms-size-popup__size-box${!isAvailable ? ' nms-size-popup__size-box--disabled' : ''}"
              title="${size.replace(/^UK\s?/i, '')}${!isAvailable ? ' (Out of Stock)' : ''}"
            >
              ${size.replace(/^UK\s?/i, '')}
            </span>
          </label>
        `;
        })
        .join('');

      this.refs.sizesContainer.innerHTML = sizesHTML;

      // Add event listeners to size inputs
      const sizeInputs = this.refs.sizesContainer.querySelectorAll('.nms-size-popup__size-input');
      sizeInputs.forEach(input => {
        input.addEventListener('change', event => this.#handleSizeSelection(event));
        
        // Add touch event handling to override cart drawer interference
        const label = input.closest('label');
        if (label) {
          label.addEventListener('touchend', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!input.disabled) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }, { passive: false });
        }
      });

      return;
    }

    // Fallback: Use original logic for backward compatibility
    const sizeOption = this.#findSizeOption();

    if (!sizeOption) {
      console.warn('[NMS] No size option found in product data');
      return;
    }

    const sizesHTML = sizeOption.values
      .map((size, index) => {
        const isAvailable = this.#isSizeAvailable(size, sizeOption.position);
        const inputId = `SizePopup-${currentProductId}-${index}`;

        return `
        <label class="nms-size-popup__size-label${!isAvailable ? ' nms-size-popup__size-label--disabled' : ''}">
          <input
            type="radio"
            name="popup-size"
            value="${size}"
            id="${inputId}"
            class="nms-size-popup__size-input sr-only"
            data-size="${size}"
            data-option-position="${sizeOption.position}"
            ${!isAvailable ? 'disabled' : ''}
          >
          <span
            class="nms-size-popup__size-box${!isAvailable ? ' nms-size-popup__size-box--disabled' : ''}"
            title="${size.replace(/^UK\s?/i, '')}${!isAvailable ? ' (Out of Stock)' : ''}"
          >
            ${size.replace(/^UK\s?/i, '')}
          </span>
        </label>
      `;
      })
      .join('');

    this.refs.sizesContainer.innerHTML = sizesHTML;

    // Add event listeners to size inputs
    const sizeInputs = this.refs.sizesContainer.querySelectorAll('.nms-size-popup__size-input');
    sizeInputs.forEach(input => {
      input.addEventListener('change', event => this.#handleSizeSelection(event));
      
      // Add touch event handling to override cart drawer interference
      const label = input.closest('label');
      if (label) {
        label.addEventListener('touchend', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!input.disabled) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, { passive: false });
      }
    });
  }

  /**
   * Find the size option in product data
   * @returns {Object|null} Size option
   * @private
   */
  #findSizeOption() {
    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];

    return this.productData.options.find(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  /**
   * Check if a size is available
   * @param {string} size - Size value
   * @param {number} optionPosition - Option position
   * @returns {boolean} Is available
   * @private
   */
  #isSizeAvailable(size, optionPosition) {
    return this.productData.variants.some(variant => {
      const variantSize = variant[`option${optionPosition}`];
      return variantSize === size && variant.available;
    });
  }

  /**
   * Handle size selection
   * @param {Event} event - Change event
   * @private
   */
  #handleSizeSelection(event) {
    const selectedSize = event.target.value;
    const optionPosition = parseInt(event.target.dataset.optionPosition);

    this.selectedSize = selectedSize;

    // Call the callback
    this.onSelect({
      size: selectedSize,
      optionPosition: optionPosition,
    });

    // Close popup after selection
    this.hide();
  }

  /**
   * Update product data (for dynamic updates)
   * @param {Object} productData - New product data
   */
  updateProductData(productData) {
    this.productData = productData;

    // Re-render if popup is open
    if (this.isOpen) {
      this.#renderSizes();
    }
  }

  /**
   * Destroy the popup
   */
  destroy() {
    super.destroy();

    // Clear the global flag if popup was open during destruction
    if (this.isOpen && window.neemans) {
      window.neemans.sizePopupOpen = false;
    }

    // No event listeners to remove - using callback-based overlay architecture

    // Remove from DOM
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Export for use
window.NMSSizePopup = NMSSizePopup;
